// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import siteConfig from '@config';
import { useUser } from '@store';

import { Action, MovePlannerStateProps } from 'src/types';
import { Moves, Offers } from '@gql/base-types';
import { isMoveLite } from '@utils/tiers';
import { capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';

import { createSingleMoveChargesInvoice, createSingleMoveInvoice } from '@services/pdfService';

import { css } from '@emotion/css';
import { Theme, useTheme, IconButton, Tooltip, Typography } from '@mui/material';
import {
  BiAddToQueue,
  BiArrowBack,
  BiBlock,
  BiReceipt,
  BiSolidEdit,
  BiSolidSave,
  BiTime,
  BiTransfer,
  BiUndo,
} from 'react-icons/bi';

import IconAction from '@components/Utils/IconAction';
import AssignOrUnassignMoveModal from '@components/Modals/AssignOrUnassignMoveModal';

import MoveDetailsToPrint from './MoveDetailsToPrint';
import {
  CancelLiteModal,
  MoveCancelModal,
  MoveTimeModal,
} from '@hopdrive/feature-move-actions';
import MockSequenceModal from '@features/moves/components/MockSequenceModal';
import { planMovesPath } from '@features/products/productRoutes';

// TYPES ---------------------------------------------------------------- //

interface MoveDetailsToolbarProps {
  move?: Moves;
  offers?: Offers[];
  isEditing?: boolean;
  onEdit?: (isEditing: boolean) => void;
  onSave?: () => void;
  onDiscard?: () => void;
  isAssigningOrUnassigning?: boolean;
  onAssignOrUnassign?: (isAssigningOrUnassigning: boolean) => void;
  /** Exposes a function to open the assign/unassign modal from external components. */
  onOpenAssignModal?: (opener: () => void) => void;
}

// COMPONENT ---------------------------------------------------------------- //

const MoveDetailsToolbar = ({
  move,
  offers,
  isEditing,
  onEdit,
  onSave,
  onDiscard,
  onAssignOrUnassign,
  onOpenAssignModal,
}: MoveDetailsToolbarProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const navigate = useNavigate();
  const user = useUser();

  // Managing (assign, cancel, edit, change time) and creating (duplicate, inverse) are gated on
  // the Valet product's capabilities: a read_only product (a lapsed contract) shows the move for
  // viewing but no write controls.
  const valetEntry = useProductEntry('moves');
  const canManage = capabilityGate(valetEntry, 'manageExisting').kind === 'render';
  const canCreate = capabilityGate(valetEntry, 'createNew').kind === 'render';

  const [timeModal, setTimeModal] = React.useState({ open: false, input: move });
  const [cancelModal, setCancelModal] = React.useState({ open: false, input: move });
  const [mockSequenceModal, setMockSequenceModal] = React.useState<{ moveId: Moves['id'] } | null>(null);
  const [assignOrUnassignModal, setAssignOrUnassignModal] = React.useState({ open: false, input: move, output: null });

  // Expose the modal opener to external components
  React.useEffect(() => {
    if (onOpenAssignModal) {
      onOpenAssignModal(() => handleAssignOrUnassignModalOpen(move));
    }
  }, [move, onOpenAssignModal]);

  // Navigation dismisses the draft; a refetch of the same move keeps it open.
  React.useEffect(() => {
    setMockSequenceModal(null);
  }, [move?.id]);

  // This useEffect is used to remove the loading state from the move after a driver is assigned/reassigned/unassigned to it
  // This is necessary because the modal doesnt wait for the backend to complete before closing
  // We do this by looking for the expected driver id on the move or latest offer after a refetch
  // Once the desired state of the move is reached, we remove the loading state from the modal's output
  React.useEffect(() => {
    if (move && assignOrUnassignModal?.output) {
      const outputDriverId = assignOrUnassignModal?.output?.driverId;
      const outputAction = assignOrUnassignModal?.output?.action;

      const foundDriverId = move?.driver_id;
      const foundOfferDriverId = offers?.[0]?.driver?.id;

      if (outputAction === 'assign' || outputAction === 'reassign') {
        if (foundDriverId === outputDriverId || foundOfferDriverId === outputDriverId) {
          setAssignOrUnassignModal({ ...assignOrUnassignModal, output: null });
          if (onAssignOrUnassign) onAssignOrUnassign(false);
        }
      }
      if (outputAction === 'unassign') {
        if (foundDriverId === outputDriverId || foundOfferDriverId === outputDriverId) {
          setAssignOrUnassignModal({ ...assignOrUnassignModal, output: null });
          if (onAssignOrUnassign) onAssignOrUnassign(false);
        }
      }
    }
  }, [move, offers]);

  // Time Modal
  const handleTimeModalOpen = () => {
    setTimeModal({ ...timeModal, open: true });
  };
  const handleTimeModalClose = () => {
    setTimeModal({ ...timeModal, open: false });
  };

  // Cancel Modal
  const handleCancelModalOpen = () => {
    setCancelModal({ ...cancelModal, open: true });
  };
  const handleCancelModalClose = () => {
    setCancelModal({ ...cancelModal, open: false });
  };

  // Mock Sequence Modal
  const handleMockSequenceModalOpen = () => {
    setMockSequenceModal(move ? { moveId: move.id } : null);
  };
  const handleMockSequenceModalClose = () => {
    // A request from an earlier opening must not close a newer draft, even for the same move.
    setMockSequenceModal(current => current === mockSequenceModal ? null : current);
  };

  // Assign or Unassign Modal
  const handleAssignOrUnassignModalOpen = (input?: any) => {
    setAssignOrUnassignModal({ ...assignOrUnassignModal, open: true, input });
  };
  const handleAssignOrUnassignModalClose = (output?: any) => {
    setAssignOrUnassignModal({ ...assignOrUnassignModal, open: false, output });
    if (onAssignOrUnassign) onAssignOrUnassign(output?.moveId === move?.id);
  };

  /** Create Duplicate Move */
  const handleCreateDuplicate = () => {
    const parentDrive = move?.parentMove;
    const childDrive = move?.childMoves?.find(cm => cm?.move_type === `drive`);

    const moveId1 = parentDrive?.id || move?.id;
    const moveId2 = childDrive?.id || move?.id;

    const state: MovePlannerStateProps = { moveId1, inverse: false };
    if (moveId2 && moveId2 !== moveId1) state.moveId2 = moveId2;

    navigate(planMovesPath(), { state });
  };

  /** Create Inverse Move */
  const handleCreateInverse = () => {
    const parentDrive = move?.parentMove;
    const childDrive = move?.childMoves?.find(cm => cm?.move_type === `drive`);

    const moveId1 = parentDrive?.id || move?.id;
    const moveId2 = childDrive?.id || move?.id;

    const state: MovePlannerStateProps = { moveId1, inverse: true };
    if (moveId2 && moveId2 !== moveId1) state.moveId2 = moveId2;

    navigate(planMovesPath(), { state });
  };

  /** Download Invoice PDF: from the move's armove invoice, else from its AR charges */
  const handleDownloadSingleMoveInvoice = async (move?: Moves) => {
    if (!move) return;

    let invoice = null;
    try {
      invoice = move?.accountsReceivable?.invoice;
    } catch (err) {
      console.error(`Failed to parse move invoice:`, err);
    }
    if (invoice) {
      createSingleMoveInvoice(invoice);
      return;
    }

    let printed = false;
    try {
      printed = await createSingleMoveChargesInvoice(move);
    } catch (err) {
      console.error(`Failed to generate move invoice PDF:`, err);
      toast.error(`Failed to generate invoice PDF!`, { toastId: 'move-details-invoice-pdf-error' });
      return;
    }
    if (!printed) toast.warn(`Could not generate invoice PDF. Please refer to the Invoices page for this move.`, { toastId: 'move-details-invoice-pdf' });
  };

  /** Handle saving changes */
  const handleSave = () => {
    if (onSave) onSave();
  };

  /** Handle editing/discarding changes */
  const handleEdit = () => {
    if (isEditing && onDiscard) onDiscard();
    if (onEdit) onEdit(!isEditing);
  };

  /** Enable the invoice button once the move is delivered, paid, billed in AR charges or fully canceled */
  const disableMoveInvoiceButton = (move?: Moves) => {
    if (!move) return false;

    const moveIsFullyCanceled = move?.cancel_status !== null && move?.cancel_status !== `pending`;
    const moveIsPaid =
      move?.accountsReceivable && move?.accountsReceivable?.status && move?.accountsReceivable?.status === `paid`;
    const moveIsBilledInCharges = (move?.archarges?.length ?? 0) > 0;
    if (move?.status === `delivery successful` || moveIsPaid || moveIsBilledInCharges || moveIsFullyCanceled) return false;
    return true;
  };

  const handleMockSequence = () => {
    handleMockSequenceModalOpen();
  };

  // List of move actions. The create and management actions render only when the Valet product
  // grants those capabilities; Download Invoice is a read action and stays regardless (so a
  // read_only product can still pull a past move's invoice). A `read_only` product yields both
  // gates absent, so it shows no create or management action.
  const actions: Action[] = [
    ...(canCreate
      ? [
          {
            label: `Create Duplicate`,
            icon: <BiAddToQueue className={cls.menuIcon} />,
            handler: () => handleCreateDuplicate(),
          },
          {
            label: `Create Inverse`,
            icon: <BiTransfer className={cls.menuIcon} />,
            handler: () => handleCreateInverse(),
          },
          {},
        ]
      : []),
    ...(canManage
      ? [
          {
            label: `Change Start Time`,
            icon: <BiTime className={cls.menuIcon} />,
            handler: () => handleTimeModalOpen(),
            disabled: move?.status !== null,
          },
          {},
        ]
      : []),
    {
      label: `Download Invoice PDF`,
      icon: <BiReceipt className={cls.menuIcon} />,
      handler: () => handleDownloadSingleMoveInvoice(move),
      disabled: disableMoveInvoiceButton(move),
    },
    {},
    // Mock Sequence drives fake status progressions — test + per-PR preview
    // envs only (throwaway DBs), matching the db-batchjobs mock gates.
    ...(siteConfig?.appEnv === 'test' || siteConfig?.appEnv === 'preview'
      ? [
          {
            label: `Mock Sequence`,
            icon: <BiTransfer className={cls.menuIcon} />,
            handler: () => handleMockSequence(),
          },
        ]
      : []),
    ...(canManage
      ? [
          ...(siteConfig?.appEnv === 'test' || siteConfig?.appEnv === 'preview' ? [{}] : []),
          {
            label: `Cancel Move`,
            icon: <BiBlock className={cls.menuIcon} />,
            color: theme?.palette?.error?.main,
            handler: () => handleCancelModalOpen(),
            disabled:
              move?.cancel_status !== null || move?.status === `delivery arrived` || move?.status === `delivery successful`,
          },
        ]
      : []),
  ];

  return (
    <>
      <MoveTimeModal
        open={timeModal?.open}
        onClose={handleTimeModalClose}
        moveInput={move}
        actor={user?.profile?.email}
      />
      {isMoveLite(move) ? (
        <CancelLiteModal
          open={cancelModal?.open}
          onClose={handleCancelModalClose}
          moveInput={move}
          actor={user?.profile?.email}
          onCreateDuplicate={state => navigate(planMovesPath(), { state })}
          onError={err => Sentry.captureException(err)}
        />
      ) : (
        <MoveCancelModal
          open={cancelModal?.open}
          onClose={handleCancelModalClose}
          moveInput={move}
          adminUrl={siteConfig.hopdrive.admin.url}
          getAuthToken={async () => user?.getToken()}
          onError={err => Sentry.captureException(err)}
        />
      )}
      <AssignOrUnassignMoveModal
        open={assignOrUnassignModal?.open}
        onClose={handleAssignOrUnassignModalClose}
        moveInput={move}
        offersInput={offers}
      />
      {move && mockSequenceModal !== null && mockSequenceModal.moveId === move.id && (
        <MockSequenceModal
          key={move.id}
          open
          onClose={handleMockSequenceModalClose}
          move={move}
        />
      )}

      <div className={cls.flex}>
        <div className={cls.flexItem}>
          <Tooltip placement='top' title={`Return to previous page`}>
            <IconButton onClick={() => navigate(-1)} sx={{ padding: `6px` }}>
              <BiArrowBack size={24} />
            </IconButton>
          </Tooltip>
        </div>

        <div className={cls.flexItem}>
          <Typography variant='h3' sx={{ fontWeight: 600 }}>
            Move Details
          </Typography>
        </div>

        <div className={cls.flexItemStretch} />

        {move ? (
          <>
            <div className={cls.flexItemButtons}>
              <MoveDetailsToPrint move={move} />
            </div>

            <div className={cls.flexItem}>
              {canManage && isEditing ? (
                <Tooltip placement='top' title={`Save your changes`}>
                  <IconButton
                    onClick={() => handleSave()}
                    sx={{
                      padding: `6px`,
                      color: theme?.palette?.secondary?.main,
                    }}
                  >
                    <BiSolidSave size={24} />
                  </IconButton>
                </Tooltip>
              ) : null}

              {canManage ? (
                <Tooltip placement='top' title={isEditing ? `Discard your changes` : `Make changes to this move`}>
                  <IconButton
                    onClick={() => handleEdit()}
                    sx={{
                      padding: `6px`,
                      color: isEditing ? theme?.palette?.warning?.main : theme?.palette?.action?.active,
                    }}
                  >
                    {isEditing ? <BiUndo size={24} /> : <BiSolidEdit size={24} />}
                  </IconButton>
                </Tooltip>
              ) : null}

              {actions.length > 0 ? (
                <Tooltip placement='top' title={`Open a list of move actions`}>
                  <IconAction actions={actions} />
                </Tooltip>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  flex: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 24px;
  `,
  flexItem: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  flexItemButtons: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    ${theme?.breakpoints?.down('sm')} {
      display: none;
    }
  `,
  flexItemStretch: css`
    flex: 1;
  `,

  menuIcon: css`
    display: block;
    margin-top: -1px;
    margin-right: 8px;
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default MoveDetailsToolbar;
